<template>
  <div
    :id="id ? id + '-alert' : null"
    :class="_class"
    :role="level >= 0 ? 'alert' : null"
    :aria-live="level >= 3 ? 'polite' : null"
    :data-label-type="alertDataLabelType"
    :tabindex="id ? '-1' : null"
    class="mts"
  >
    <template v-if="message || $slots.default">
      <div class="errors-body">
        <div v-if="!plain" aria-hidden="true" :class="iconClass"></div>
        <span class="visually-hidden">{{ alertSrText }}</span>
        <div class="errors-text">
          <slot>{{ message }}</slot>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Inject } from "vue-facing-decorator";
import { defaultResolveTxt } from "@/components/field-element";
import { PropOptions } from "vue";
@Component({ name: "cat-error" })
export default class CatError extends Vue {
  @Prop({
    type: [String, Number],
    description: "The node the error container is associated to."
  } as PropOptions)
  readonly id?: string | number;
  @Prop({
    type: Number,
    description: "The alert styling applied to the alert message wrapper (1-4)."
  } as PropOptions)
  readonly level?: number;
  @Prop({
    type: String,
    default: null,
    description: "Displays error message."
  } as PropOptions)
  readonly message?: string;
  @Prop({
    type: Boolean,
    default: true,
    description: "Centers error message in box."
  } as PropOptions)
  readonly center?: boolean;
  @Prop({
    type: Boolean,
    default: false,
    description: "Displays plain with no background color."
  } as PropOptions)
  readonly plain?: boolean;
  @Prop({
    type: String,
    default: null,
    description:
      "The name of the icon as listed in the iconography. If specified, overrides the default icon of the alert level."
  } as PropOptions)
  readonly alertIcon?: string;
  @Prop({
    type: String,
    default: null,
    description:
      "The accessible name of the alert icon for assistive technologies, not rendered visually on the screen. If not specified, the default accessible name corresponding to the alert level is used."
  } as PropOptions)
  readonly alertIconOffscreenLabel?: string;
  @Prop({
    type: Boolean,
    default: true,
    description:
      "Whether to show the border to the alert container with the corresponding border colour for the alert level."
  } as PropOptions)
  readonly outlined?: boolean;
  @Prop({
    type: Boolean,
    default: false,
    description:
      "Whether to have transparent background for the alert container."
  } as PropOptions)
  readonly transparent?: boolean;

  @Inject({
    default: defaultResolveTxt,
    from: "resolveTxt"
  })
  readonly resolveTxt!: Function;

  get _class(): object {
    return {
      "hide-empty": true,
      fatal: this.level == 0,
      error: this.level == 1,
      warn: this.level == 2,
      info: this.level == 3,
      debug: this.level == 4,
      "cat-alert": true,
      "cat-error": true,
      "text-align-center": this.center,
      "no-outline": !this.outlined,
      "no-background": this.transparent,
      "custom-alert-icon": this.alertIcon,
      plain: this.plain
    };
  }

  get iconClass(): object {
    let css = {
      "errors-container-icon": true
    };
    if (this.alertIcon) {
      css["custom-icon"] = true;
      css["icon-" + this.alertIcon] = true;
    }
    return css;
  }

  get alertDataLabelType(): string {
    switch (this.level) {
      case 0:
        return "alert-fatal";
      case 1:
        return "alert-error";
      case 2:
        return "alert-warning";
      case 3:
        return "alert-info";
      case 4:
        return "alert-success";
      default:
        return "alert-default";
    }
  }

  get alertSrText(): string {
    if (this.alertIconOffscreenLabel) {
      return this.alertIconOffscreenLabel;
    }
    switch (this.level) {
      case 0:
      case 1:
        return this.resolveTxt("skin.alert.icon.offscreenLabel.error", "Error");
      case 2:
        return this.resolveTxt(
          "skin.alert.icon.offscreenLabel.warning",
          "Warning"
        );
      case 3:
        return this.resolveTxt(
          "skin.alert.icon.offscreenLabel.info",
          "Information"
        );
      case 4:
        return this.resolveTxt(
          "skin.alert.icon.offscreenLabel.success",
          "Success"
        );
      default:
        return this.resolveTxt(
          "skin.alert.icon.offscreenLabel.default",
          "Note"
        );
    }
  }
}
</script>
